import { Component, effect, inject, model, OnDestroy, OnInit, signal } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { AgentsSearchFormGroup } from '@shared/types/points';
import { finalize, map, filter } from 'rxjs';
import { PointsAdvancedSearchService } from '../services/points-advanced-search.service';
import { UserDto } from '@shared/interfaces/dtos/users';
import { UsersService } from '@shared/services/users.service';
import { extractElementsFromQueryParam } from '@shared/helpers';
import { sortBy } from 'lodash';

@Component({
  selector: 'idel-agents',
  templateUrl: './agents.component.html',
  styleUrl: './agents.component.scss',
})
export class AgentsComponent implements OnInit, OnDestroy {
  private readonly pointsAdvancedSearchService = inject(PointsAdvancedSearchService);
  private readonly usersService = inject(UsersService);

  public readonly form = model.required<FormGroup<AgentsSearchFormGroup>>();

  public readonly hasToIncludeInactiveAgents = signal<boolean>(false);
  public readonly hasToIncludeInactiveUsers = signal<boolean>(false);
  public readonly fetchingAgents = signal<boolean>(false);
  public readonly fetchingUsers = signal<boolean>(false);
  public readonly agents = signal<UserDto[]>([]);
  public readonly users = signal<UserDto[]>([]);

  private readonly shouldPopulate = this.pointsAdvancedSearchService.shouldPopulateForms;
  private readonly shouldPopulateEff = effect(
    () => {
      const shouldPopulate = this.shouldPopulate();

      if (shouldPopulate) {
        this.populateFromCurrentSearch();
      }
    },
    { allowSignalWrites: true }
  );

  ngOnInit(): void {
    this.fetchUsers();
    this.fetchAgents();
  }

  ngOnDestroy(): void {
    this.shouldPopulateEff.destroy();
  }

  private populateFromCurrentSearch(): void {
    const currentSearch = this.pointsAdvancedSearchService.currentSearch();

    if (!currentSearch) return;

    this.pointsAdvancedSearchService.setFormPopulationProgressAsStartedFor('agents');

    const selectedAgents = this.agents().filter((agent) => {
      const selectedAgentsIds = extractElementsFromQueryParam(currentSearch.AgentTraitantIdiAdmins ?? []);

      if (!selectedAgentsIds.length) return false;

      return selectedAgentsIds.includes(agent.contactForUser.contactId.toString());
    });

    const areAgentsActive = selectedAgents.every((agent) => agent.active);
    this.hasToIncludeInactiveAgents.set(!areAgentsActive);

    const selectedUsers = this.users().filter((user) => {
      const selectedUsersIds = extractElementsFromQueryParam(currentSearch.CreeParContactId ?? []);

      if (!selectedUsersIds.length) return false;

      return selectedUsersIds.includes(user.contactForUser.contactId.toString());
    });

    const areUsersActive = selectedUsers.every((user) => user.active);
    this.hasToIncludeInactiveUsers.set(!areUsersActive);

    this.form().patchValue({
      agents: selectedAgents ?? [],
      users: selectedUsers ?? [],
    });

    this.pointsAdvancedSearchService.setFormPopulationProgressAsDoneFor('agents');
  }

  private fetchUsers(): void {
    this.fetchingUsers.set(true);

    this.usersService
      .fetchUsers()
      .pipe(
        map((res: UserDto[]) => res.filter((user) => (this.hasToIncludeInactiveUsers() ? user.active : true))),
        finalize(() => this.fetchingUsers.set(false))
      )
      .subscribe({
        next: (users) =>
          this.users.set(
            sortBy(
              users.map((user) => ({ ...user, nom: user.contactForUser.nom.toLowerCase() })),
              ['nom']
            )
          ),
      });
  }

  private fetchAgents(): void {
    this.fetchingAgents.set(true);

    this.usersService
      .fetchUsers()
      .pipe(
        finalize(() => this.fetchingAgents.set(false)),
        map((res) => res.filter((user) => (this.hasToIncludeInactiveAgents() ? user.active : true)))
      )
      .subscribe({
        next: (users) => {
          console.log(users);

          this.agents.set(
            sortBy(
              users.map((user) => ({ ...user, nom: user.contactForUser.nom.toLowerCase() })),
              ['nom']
            )
          );
        },
      });
  }

  public toggleHasToIncludeInactiveAgents(): void {
    this.form().controls.agents.setValue([]);
    this.hasToIncludeInactiveAgents.set(!this.hasToIncludeInactiveAgents());
    this.fetchAgents();
  }

  public toggleHasToIncludeInactiveUsers(): void {
    this.form().controls.users.setValue([]);
    this.hasToIncludeInactiveUsers.set(!this.hasToIncludeInactiveUsers());
    this.fetchUsers();
  }
}
